import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Input, Select, TextArea, LoadingButton, SimpleButton, Spinner, useStateContext } from '@veda/dashboard-ui';
import {
  MdCheckCircle, MdRadioButtonUnchecked, MdUploadFile, MdDelete,
  MdChevronLeft, MdChevronRight, MdSend,
} from 'react-icons/md';
import { usePropostaApi } from '../services/propostaApi';
import { usePppAiContext } from '../../ppp/hooks/usePppAiContext';

// ─── Tipi documentali usati dalla proposta (art. 193) ──────────────────────────
const TIPO = {
  PFTE:    'pfte',
  PEF:     'piano_economico_finanziario',
  CONTRATTO: 'schema_contratto',
  RELAZIONE: 'relazione_tecnica',
  STUDIO:    'studio_fattibilita',
};

const STEPS = [
  { n: 1, titolo: 'Dati generali' },
  { n: 2, titolo: 'PEF asseverato' },
  { n: 3, titolo: 'Documentazione tecnica' },
  { n: 4, titolo: 'Bozza convenzione' },
  { n: 5, titolo: 'Dichiarazioni e invio' },
];

// ─── Riga di upload per un tipo documento ──────────────────────────────────────
// Il flag "parti riservate" è una proprietà persistente del documento caricato
// (d.contieneDatiRiservati): la checkbox lo legge e lo scrive via backend, così
// non si perde navigando tra gli step.
function UploadRiga({ label, tipo, documenti, onUpload, onDelete, onToggleRiservato, riservabile = false }) {
  const presenti = documenti.filter((d) => d.tipo === tipo);
  const [busy, setBusy] = useState(false);
  const ref = React.useRef(null);

  const scegli = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    onUpload(file, tipo, false, () => { setBusy(false); if (ref.current) ref.current.value = ''; });
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{label}</span>
        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 text-sm font-medium cursor-pointer transition-colors">
          <MdUploadFile /> {busy ? 'Caricamento…' : 'Carica file'}
          <input ref={ref} type="file" className="hidden" onChange={scegli} disabled={busy} />
        </label>
      </div>
      {presenti.length > 0 && (
        <ul className="mt-3 space-y-2">
          {presenti.map((d) => (
            <li key={d.id} className="bg-gray-50 dark:bg-gray-700/40 rounded px-3 py-2">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                  <MdCheckCircle className="text-green-500" /> {d.nome}
                </span>
                <button onClick={() => onDelete(d.id)} className="text-red-500 hover:text-red-700"><MdDelete /></button>
              </div>
              {riservabile && (
                <label className="flex items-center gap-2 mt-1.5 text-xs text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!d.contieneDatiRiservati}
                    onChange={(e) => onToggleRiservato(d.id, e.target.checked)}
                  />
                  Contiene parti riservate (segreto tecnico/commerciale, art. 35-36)
                </label>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CheckRiga({ checked, onChange, children }) {
  return (
    <label className="flex items-start gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer">
      <input type="checkbox" className="mt-1" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-sm text-gray-700 dark:text-gray-200">{children}</span>
    </label>
  );
}

export default function WizardProposta() {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = usePropostaApi();
  const { addToast } = useStateContext();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [propostaId, setPropostaId] = useState(id ?? null);
  const [procedimenti, setProcedimenti] = useState([]);
  const [documenti, setDocumenti] = useState([]);
  const [completezza, setCompletezza] = useState(null);
  const dichiarazioniSaveTimer = useRef(null);

  usePppAiContext('ppp.proposte', propostaId ? { propostaId } : {});

  const [form, setForm] = useState({
    procedimentoId: '',
    titolo: '',
    tipoProposta: 'spontanea',
    descrizione: '',
    importoSpese: '',
    pefAsseveratore: '',
    pefDataAsseverazione: '',
    dichiarazioneArt68: false,
    dichiarazioneAssenzaEsclusioni: false,
    dichiarazioneVeridicita: false,
    stato: 'bozza',
  });
  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e?.target ? e.target.value : e }));

  // Salva le dichiarazioni in debounce quando l'utente le spunta allo step 5.
  // Necessario perché allo step 5 non c'è "Salva e continua" e la completezza
  // viene calcolata dal backend (non localmente).
  const setDichiarazione = (campo) => (valore) => {
    setForm((f) => {
      const nuovoForm = { ...f, [campo]: valore };
      if (propostaId) {
        clearTimeout(dichiarazioniSaveTimer.current);
        dichiarazioniSaveTimer.current = setTimeout(() => {
          api.aggiornaBozza(propostaId, {
            titolo: nuovoForm.titolo,
            tipoProposta: nuovoForm.tipoProposta,
            descrizione: nuovoForm.descrizione || null,
            importoSpese: nuovoForm.importoSpese === '' ? null : Number(nuovoForm.importoSpese),
            pefAsseveratore: nuovoForm.pefAsseveratore || null,
            pefDataAsseverazione: nuovoForm.pefDataAsseverazione || null,
            dichiarazioneArt68: nuovoForm.dichiarazioneArt68,
            dichiarazioneAssenzaEsclusioni: nuovoForm.dichiarazioneAssenzaEsclusioni,
            dichiarazioneVeridicita: nuovoForm.dichiarazioneVeridicita,
          }, {
            onSuccess: () => ricaricaCompletezza(propostaId),
            onFormError: () => {},
          });
        }, 300);
      }
      return nuovoForm;
    });
  };

  const toast = (testo, tipo = 'success') => addToast?.({ type: tipo, title: testo });

  // Carica procedimenti aperti + eventuale bozza esistente
  useEffect(() => {
    api.procedimentiAperti({
      onSuccess: (data) => setProcedimenti(Array.isArray(data) ? data : []),
      onFormError: () => {},
    });
    if (id) {
      api.getProposta(id, {
        onSuccess: (p) => {
          // Il wizard è solo per le bozze: una proposta già presentata si
          // visualizza nella vista avanzamento (sola lettura).
          if (p.stato && p.stato !== 'bozza') {
            navigate(`/operatore/proposte/${id}`, { replace: true });
            return;
          }
          setForm((f) => ({
            ...f,
            procedimentoId: String(p.procedimentoId ?? ''),
            titolo: p.titolo ?? '',
            tipoProposta: p.tipoProposta ?? 'spontanea',
            descrizione: p.descrizione ?? '',
            importoSpese: p.importoSpese ?? '',
            pefAsseveratore: p.pefAsseveratore ?? '',
            pefDataAsseverazione: p.pefDataAsseverazione ?? '',
            dichiarazioneArt68: !!p.dichiarazioneArt68,
            dichiarazioneAssenzaEsclusioni: !!p.dichiarazioneAssenzaEsclusioni,
            dichiarazioneVeridicita: !!p.dichiarazioneVeridicita,
            stato: p.stato ?? 'bozza',
          }));
          setDocumenti(Array.isArray(p.documenti) ? p.documenti : []);
          setLoading(false);
        },
        onFormError: () => { toast('Proposta non trovata.', 'error'); navigate('/operatore/proposte'); },
      });
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // NB: funzioni normali (non useCallback con deps vuote): così usano sempre
  // l'istanza `api`/`request` del render corrente. Con useCallback([]) avrebbero
  // catturato il request del primo render, e dopo l'upload la lista documenti /
  // la completezza non si aggiornavano finché un altro render non interveniva
  // (es. spunta del checkbox "parti riservate").
  const ricaricaDocumenti = (pid) => {
    api.listDocumenti(pid, {
      onSuccess: (d) => setDocumenti(Array.isArray(d) ? d : []),
      onFormError: () => {},
    });
  };

  const ricaricaCompletezza = (pid) => {
    api.completezza(pid, {
      onSuccess: (c) => setCompletezza(c),
      onFormError: () => {},
    });
  };

  // Salva lo step corrente: crea la bozza al primo salvataggio, poi aggiorna.
  const salva = (onDone) => {
    setSaving(true);
    const payload = {
      titolo: form.titolo,
      tipoProposta: form.tipoProposta,
      descrizione: form.descrizione || null,
      importoSpese: form.importoSpese === '' ? null : Number(form.importoSpese),
      pefAsseveratore: form.pefAsseveratore || null,
      pefDataAsseverazione: form.pefDataAsseverazione || null,
      dichiarazioneArt68: form.dichiarazioneArt68,
      dichiarazioneAssenzaEsclusioni: form.dichiarazioneAssenzaEsclusioni,
      dichiarazioneVeridicita: form.dichiarazioneVeridicita,
    };

    if (!propostaId) {
      api.creaBozza(form.procedimentoId, {
        titolo: form.titolo,
        tipoProposta: form.tipoProposta,
        descrizione: form.descrizione || null,
        importoSpese: payload.importoSpese,
      }, {
        onSuccess: (res) => {
          const nuovoId = res?.id;
          // NB: l'id resta solo nello stato locale. NON cambiamo l'URL (né con
          // replaceState né con navigate): passare da /nuova a /:id/modifica
          // rimonterebbe il componente azzerando il form (spunte/dichiarazioni perse).
          setPropostaId(nuovoId);
          setSaving(false);
          toast('Bozza creata.');
          onDone?.(nuovoId);
        },
        onFormError: (err) => { setSaving(false); toast(err?.message ?? 'Errore nel salvataggio.', 'error'); },
      });
    } else {
      api.aggiornaBozza(propostaId, payload, {
        onSuccess: () => { setSaving(false); onDone?.(propostaId); },
        onFormError: (err) => { setSaving(false); toast(err?.message ?? 'Errore nel salvataggio.', 'error'); },
      });
    }
  };

  const avanti = () => {
    salva((pid) => {
      if (step < 5) setStep(step + 1);
      if (pid) { ricaricaDocumenti(pid); ricaricaCompletezza(pid); }
    });
  };
  const indietro = () => setStep((s) => Math.max(1, s - 1));

  const upload = (file, tipo, riservato, done) => {
    if (!propostaId) { toast('Salva prima i dati generali (step 1).', 'error'); done?.(); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('tipo', tipo);
    fd.append('contiene_dati_riservati', riservato ? '1' : '0');
    api.uploadDocumento(propostaId, fd, {
      onSuccess: () => { ricaricaDocumenti(propostaId); ricaricaCompletezza(propostaId); toast('Documento caricato.'); done?.(); },
      onError:   () => { toast('Errore upload.', 'error'); done?.(); },
      onFormError: () => { toast('Errore upload.', 'error'); done?.(); },
    });
  };

  const eliminaDoc = (documentoId) => {
    api.eliminaDocumento(propostaId, documentoId, {
      onSuccess: () => { ricaricaDocumenti(propostaId); ricaricaCompletezza(propostaId); },
      onFormError: () => {},
    });
  };

  const toggleRiservato = (documentoId, valore) => {
    // Aggiornamento ottimistico locale + persistenza backend.
    setDocumenti((docs) => docs.map((d) => d.id === documentoId ? { ...d, contieneDatiRiservati: valore } : d));
    api.aggiornaDocumento(propostaId, documentoId, { contiene_dati_riservati: valore }, {
      onSuccess: () => {},
      onFormError: () => { ricaricaDocumenti(propostaId); toast('Errore aggiornamento documento.', 'error'); },
    });
  };

  const invia = () => {
    salva((pid) => {
      setSaving(true);
      api.presenta(pid, {
        onSuccess: (res) => {
          setSaving(false);
          toast(`Proposta presentata. Protocollo ${res?.numeroProtocollo ?? ''}.`);
          navigate('/operatore/proposte');
        },
        onFormError: (err) => { setSaving(false); toast(err?.message ?? 'Proposta incompleta.', 'error'); },
      });
    });
  };

  if (loading) return <div className="flex items-center justify-center min-h-64"><Spinner /></div>;

  const step1Valido = form.procedimentoId && form.titolo;
  const completa = completezza?.completa === true;

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
      <button onClick={() => navigate('/operatore/proposte')} className="mb-4 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
        <MdChevronLeft /> Le mie proposte
      </button>

      <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-1">
        {propostaId ? 'Compila proposta PPP' : 'Nuova proposta PPP'}
      </h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Presentazione ai sensi dell'art. 193 D.lgs 36/2023</p>

      {/* Indicatore step */}
      <div className="flex items-center gap-2 mb-8 flex-wrap">
        {STEPS.map((s) => (
          <button
            key={s.n}
            onClick={() => propostaId && setStep(s.n)}
            disabled={!propostaId && s.n !== 1}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
              step === s.n ? 'bg-blue-600 text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 disabled:opacity-50'}`}
          >
            <span className="font-semibold">{s.n}</span> {s.titolo}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 space-y-4 max-w-3xl">
        {/* STEP 1 — Dati generali */}
        {step === 1 && (
          <>
            <Select
              label="Procedimento / Ente destinatario *"
              value={form.procedimentoId}
              onChange={set('procedimentoId')}
              options={[
                { value: '', label: 'Seleziona un procedimento…' },
                ...procedimenti.map((p) => ({ value: String(p.id), label: `${p.titolo} — ${p.enteConcedente ?? ''}` })),
              ]}
            />
            <Input label="Titolo della proposta *" value={form.titolo} onChange={set('titolo')} />
            <Select
              label="Tipo proposta"
              value={form.tipoProposta}
              onChange={set('tipoProposta')}
              options={[
                { value: 'spontanea', label: 'Spontanea (art. 193 co. 3)' },
                { value: 'risposta_avviso', label: 'In risposta ad avviso (art. 193 co. 16)' },
              ]}
            />
            <TextArea label="Descrizione" value={form.descrizione} onChange={set('descrizione')} />
            <Input label="Importo spese di predisposizione (€)" type="number" value={form.importoSpese} onChange={set('importoSpese')} />
          </>
        )}

        {/* STEP 2 — PEF asseverato */}
        {step === 2 && (
          <>
            <p className="text-sm text-gray-500">Il Piano Economico-Finanziario asseverato è un requisito essenziale e non integrabile (art. 193).</p>
            <UploadRiga label="Piano Economico-Finanziario asseverato" tipo={TIPO.PEF} documenti={documenti} onUpload={upload} onDelete={eliminaDoc} />
            <Input label="Soggetto asseveratore *" value={form.pefAsseveratore} onChange={set('pefAsseveratore')} />
            <Input label="Data asseverazione *" type="date" value={form.pefDataAsseverazione} onChange={set('pefDataAsseverazione')} />
          </>
        )}

        {/* STEP 3 — Documentazione tecnica */}
        {step === 3 && (
          <>
            <p className="text-sm text-gray-500">Il PFTE (Progetto di Fattibilità Tecnico-Economica) è obbligatorio (art. 193 co. 6-bis).</p>
            <UploadRiga label="PFTE — Progetto di Fattibilità Tecnico-Economica *" tipo={TIPO.PFTE} documenti={documenti} onUpload={upload} onDelete={eliminaDoc} onToggleRiservato={toggleRiservato} riservabile />
            <UploadRiga label="Relazione tecnica" tipo={TIPO.RELAZIONE} documenti={documenti} onUpload={upload} onDelete={eliminaDoc} onToggleRiservato={toggleRiservato} riservabile />
            <UploadRiga label="Studio di fattibilità" tipo={TIPO.STUDIO} documenti={documenti} onUpload={upload} onDelete={eliminaDoc} onToggleRiservato={toggleRiservato} riservabile />
          </>
        )}

        {/* STEP 4 — Bozza convenzione */}
        {step === 4 && (
          <>
            <p className="text-sm text-gray-500">Bozza di convenzione (schema di contratto): durata, ripartizione rischi, penali, garanzie, revisione, recesso.</p>
            <UploadRiga label="Bozza di convenzione *" tipo={TIPO.CONTRATTO} documenti={documenti} onUpload={upload} onDelete={eliminaDoc} onToggleRiservato={toggleRiservato} riservabile />
          </>
        )}

        {/* STEP 5 — Dichiarazioni + invio */}
        {step === 5 && (
          <>
            <h2 className="font-semibold text-gray-700 dark:text-gray-200">Autodichiarazioni</h2>
            <CheckRiga checked={form.dichiarazioneArt68} onChange={setDichiarazione('dichiarazioneArt68')}>
              Dichiaro il possesso dei requisiti di cui all'art. 68 D.lgs 36/2023.
            </CheckRiga>
            <CheckRiga checked={form.dichiarazioneAssenzaEsclusioni} onChange={setDichiarazione('dichiarazioneAssenzaEsclusioni')}>
              Dichiaro l'assenza di cause di esclusione.
            </CheckRiga>
            <CheckRiga checked={form.dichiarazioneVeridicita} onChange={setDichiarazione('dichiarazioneVeridicita')}>
              Dichiaro la veridicità di tutti i dati e documenti forniti.
            </CheckRiga>

            {/* Riepilogo completezza */}
            {completezza && (
              <div className="mt-4 border-t border-gray-100 dark:border-gray-700 pt-4">
                <h3 className="font-semibold text-gray-700 dark:text-gray-200 mb-2">Requisiti per la presentazione</h3>
                <ul className="space-y-1">
                  {completezza.requisiti.map((r) => (
                    <li key={r.chiave} className="flex items-center gap-2 text-sm">
                      {r.soddisfatto
                        ? <MdCheckCircle className="text-green-500" />
                        : <MdRadioButtonUnchecked className="text-gray-400" />}
                      <span className={r.soddisfatto ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400'}>{r.etichetta}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {/* Navigazione */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
          <SimpleButton customFunc={step > 1 ? indietro : undefined} text="Indietro" icon={<MdChevronLeft />} />
          {step < 5 ? (
            <LoadingButton
              isLoading={saving}
              customFunc={step1Valido ? avanti : undefined}
              text="Salva e continua"
              icon={<MdChevronRight />}
            />
          ) : (
            <LoadingButton
              isLoading={saving}
              customFunc={completa ? invia : undefined}
              text="Invia proposta"
              icon={<MdSend />}
            />
          )}
        </div>
        {step === 5 && !completa && (
          <p className="text-xs text-amber-600 text-right">Completa tutti i requisiti per abilitare l'invio.</p>
        )}
      </div>
    </div>
  );
}
